import {useSearchParams} from 'react-router-dom';
import {useCallback, useMemo} from 'react';
import {SingleValue} from 'react-select';
import {formatEuro} from '../../../support/format_currency';

interface PriceOption {
    label: string;
    value: number;
}
export function usePrices(priceFrom: number | undefined, priceTill: number | undefined) {
    const priceOptions: PriceOption[] = useMemo(() => {
        const prices = [
            2500, 5000, 6000, 7000, 8000, 9000, 10000, 12500, 15000, 17500, 20000, 25000, 30000, 35000, 40000, 45000,
            50000, 60000, 75000, 100000, 150000,
        ];

        return prices.map((price) => {
            return {
                label: formatEuro(price),
                value: price,
            };
        });
    }, []);

    const priceFromOptions = useMemo(() => {
        return priceOptions.filter((price) => {
            if (priceTill == null) {
                return true;
            }
            return priceTill > price.value;
        });
    }, [priceOptions, priceTill]);
    const priceTillOptions = useMemo(() => {
        return priceOptions.filter((price) => {
            if (priceFrom == null) {
                return true;
            }
            return priceFrom < price.value;
        });
    }, [priceOptions, priceFrom]);

    const selectedPriceFromOption = useMemo(() => {
        return priceFrom != null ? priceFromOptions.find((option) => option.value === priceFrom) : null;
    }, [priceFrom, priceFromOptions]);
    const selectedPriceTillOption = useMemo(() => {
        return priceTill != null ? priceTillOptions.find((option) => option.value === priceTill) : null;
    }, [priceTill, priceTillOptions]);

    const [, setSearchParams] = useSearchParams();
    const onSetPriceFromCallback = useCallback(
        (value: SingleValue<PriceOption>) => {
            setSearchParams((current) => {
                if (value?.value) {
                    current.set('price_from', value.value.toString());
                } else {
                    current.delete('price_from');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );
    const onSetPriceTillCallback = useCallback(
        (value: SingleValue<PriceOption>) => {
            setSearchParams((current) => {
                if (value?.value) {
                    current.set('price_till', value.value.toString());
                } else {
                    current.delete('price_till');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );

    return {
        priceFromOptions,
        priceTillOptions,
        selectedPriceFromOption,
        selectedPriceTillOption,
        onSetPriceFromCallback,
        onSetPriceTillCallback,
    };
}
